import { useCallback, useEffect, useState } from "react"

import { api, type AutorNaTrilha, type LinhaDaTrilhaNaTela } from "../api"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import { Card, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ComDica } from "../componentes/ComDica"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import { Escolha } from "../componentes/Escolha"
import * as Icones from "../componentes/Icones"
import { CarregandoNaSecao, FalhaNaSecao } from "../componentes/Secao"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Column, TablePrincipal2 } from "../componentes/TablePrincipal2"
import { dataHora, inteiro } from "../formato"
import { BOTAO_BRANCO, CABECALHO_DO_SHEET, MAIUSCULA_INLINE } from "../lib/classes"
import { cn } from "../lib/cn"

const POR_PAGINA = 25
const TAMANHO_DO_RESUMO = 60
const FILTRO_DA_BARRA = "w-full min-w-0 md:w-60 md:flex-none"
const DATA_DA_BARRA = "w-full min-w-0 md:w-40 md:flex-none"

type Filtros = { autor: string; tabela: string; de: string; ate: string }

const SEM_FILTRO: Filtros = { autor: "", tabela: "", de: "", ate: "" }

const resumo = (texto: string | null) =>
  texto === null ? "—" : texto.length > TAMANHO_DO_RESUMO ? `${texto.slice(0, TAMANHO_DO_RESUMO).trimEnd()}…` : texto

function quem(linha: LinhaDaTrilhaNaTela): string {
  if (linha.autor_nome !== null) return linha.autor_nome
  return linha.autor_email !== null ? `Rotina do sistema · ${linha.autor_email}` : "Rotina do sistema"
}

function sobre(linha: LinhaDaTrilhaNaTela): string {
  return [
    linha.tabela_rotulo,
    linha.registro_id > 0 ? `#${linha.registro_id}` : null,
    linha.campo_rotulo !== null ? `· ${linha.campo_rotulo}` : null,
  ]
    .filter(Boolean)
    .join(" ")
}

export function Auditoria() {
  const [linhas, definirLinhas] = useState<LinhaDaTrilhaNaTela[] | null>(null)
  const [total, definirTotal] = useState(0)
  const [autores, definirAutores] = useState<AutorNaTrilha[]>([])
  const [tabelas, definirTabelas] = useState<string[]>([])
  const [rotulosDasTabelas, definirRotulosDasTabelas] = useState<Record<string, string>>({})
  const [falha, definirFalha] = useState<string | null>(null)
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(POR_PAGINA)
  const [filtros, definirFiltros] = useState<Filtros>(SEM_FILTRO)
  const [aberta, definirAberta] = useState<LinhaDaTrilhaNaTela | null>(null)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .auditoria({
        pagina,
        por_pagina: porPagina,
        autor_id: filtros.autor === "" ? null : Number(filtros.autor),
        tabela: filtros.tabela === "" ? undefined : filtros.tabela,
        de: filtros.de === "" ? undefined : filtros.de,
        ate: filtros.ate === "" ? undefined : filtros.ate,
      })
      .then((resposta) => {
        definirLinhas(resposta.dados)
        definirTotal(resposta.total)
        definirAutores(resposta.autores)
        definirTabelas(resposta.tabelas)
        definirRotulosDasTabelas(resposta.tabelas_rotulos)
      })
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [pagina, porPagina, filtros])

  useEffect(carregar, [carregar])

  const temFiltro = filtros.autor !== "" || filtros.tabela !== "" || filtros.de !== "" || filtros.ate !== ""
  const filtrar = (novos: Partial<Filtros>) => {
    definirFiltros((atuais) => ({ ...atuais, ...novos }))
    definirPagina(1)
  }

  const cabecalho = (
    <CardHeader>
      <CardTitle>
        Trilha de auditoria{" "}
        <ComDica dica="Todo ato que muda um dado fica registrado aqui: quando, quem, o que fez, sobre o quê, o valor de antes e o de depois e de onde. Ninguém altera nem apaga um registro. Clique na linha para ver o registro inteiro." />
      </CardTitle>
    </CardHeader>
  )

  if (linhas === null) {
    return (
      <Card>
        {cabecalho}
        <CardContent>{falha !== null ? <FalhaNaSecao mensagem={falha} aoTentar={carregar} /> : <CarregandoNaSecao />}</CardContent>
      </Card>
    )
  }

  const totalDePaginas = Math.max(1, Math.ceil(total / porPagina))
  const vazio = (
    <Empty className="py-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">{temFiltro ? <Icones.Buscar /> : <Icones.Historico />}</EmptyMedia>
        <EmptyTitle>{temFiltro ? "Nada encontrado" : "Nenhum ato registrado"}</EmptyTitle>
        <EmptyDescription>{temFiltro ? "Nenhum ato casa com este filtro." : "Nenhum ato praticado nesta entidade ainda."}</EmptyDescription>
      </EmptyHeader>
      {temFiltro && (
        <EmptyContent>
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => filtrar(SEM_FILTRO)}>
            <Icones.Fechar />
            Limpar filtros
          </Button>
        </EmptyContent>
      )}
    </Empty>
  )

  return (
    <Card>
      {cabecalho}
      <CardContent>
        {falha !== null && (
          <div className="mb-3">
            <FalhaNaSecao mensagem={falha} aoTentar={carregar} />
          </div>
        )}
        <TablePrincipal2
          data={linhas}
          pageCount={totalDePaginas}
          currentPage={pagina}
          pageSize={porPagina}
          totalRegistros={total}
          onPageChange={definirPagina}
          onPageSizeChange={(quantos) => {
            definirPorPagina(quantos)
            definirPagina(1)
          }}
          listrada
          mensagemVazia={vazio}
          aoClicarNaLinha={definirAberta}
          filtrosNaBarra={
            <>
              <Escolha
                classeDaCaixa={FILTRO_DA_BARRA}
                name="trilha-autor"
                rotulo="Filtrar por autor"
                valor={filtros.autor}
                aoMudar={(autor) => filtrar({ autor })}
                vazio="Todos os autores"
                placeholderDaBusca="Buscar pelo nome"
                opcoes={autores
                  .filter((a) => a.autor_id !== null)
                  .map((a) => ({ valor: String(a.autor_id), rotulo: `${a.nome} (${inteiro(a.quantas)})` }))}
              />
              <Escolha
                classeDaCaixa={FILTRO_DA_BARRA}
                name="trilha-tabela"
                rotulo="Filtrar pelo que foi tocado"
                valor={filtros.tabela}
                aoMudar={(tabela) => filtrar({ tabela })}
                vazio="Tudo o que foi tocado"
                placeholderDaBusca="Buscar pelo que foi tocado"
                opcoes={tabelas.map((t) => ({ valor: t, rotulo: rotulosDasTabelas[t] ?? t }))}
              />
              <CampoDeData
                name="trilha-de"
                rotulo="A partir de"
                placeholder="A partir de"
                classeDaCaixa={DATA_DA_BARRA}
                valor={filtros.de}
                max={filtros.ate || undefined}
                aoMudar={(de) => filtrar({ de })}
              />
              <CampoDeData
                name="trilha-ate"
                rotulo="Até"
                placeholder="Até"
                classeDaCaixa={DATA_DA_BARRA}
                valor={filtros.ate}
                min={filtros.de || undefined}
                aoMudar={(ate) => filtrar({ ate })}
              />
              {temFiltro && (
                <Button type="button" variant="ghost" className="h-10 text-destructive hover:bg-muted hover:text-destructive" onClick={() => filtrar(SEM_FILTRO)}>
                  <Icones.Fechar />
                  Limpar filtros
                </Button>
              )}
            </>
          }
        >
          <Column<LinhaDaTrilhaNaTela>
            name="momento"
            label="Quando"
            enableSorting={false}
            render={(valor) => <span className="whitespace-nowrap tabular-nums">{dataHora(String(valor))}</span>}
          />
          <Column<LinhaDaTrilhaNaTela>
            name="autor_nome"
            label="Quem"
            enableSorting={false}
            render={(_valor, linha) => <span className={linha.autor_nome === null ? "text-(--tinta-2) italic" : undefined}>{quem(linha)}</span>}
          />
          <Column<LinhaDaTrilhaNaTela>
            name="operacao_rotulo"
            label="O quê"
            enableSorting={false}
            render={(valor) => <span className={MAIUSCULA_INLINE}>{String(valor)}</span>}
          />
          <Column<LinhaDaTrilhaNaTela>
            name="tabela_rotulo"
            label="Sobre"
            enableSorting={false}
            render={(_valor, linha) => <span className={MAIUSCULA_INLINE}>{sobre(linha)}</span>}
          />
          <Column<LinhaDaTrilhaNaTela>
            name="valor_anterior"
            label="De"
            enableSorting={false}
            render={(valor) => <span className="text-(--tinta-2)">{resumo(valor as string | null)}</span>}
          />
          <Column<LinhaDaTrilhaNaTela>
            name="valor_novo"
            label="Para"
            enableSorting={false}
            render={(valor) => resumo(valor as string | null)}
          />
          <Column<LinhaDaTrilhaNaTela>
            name="ato"
            label="Onde"
            enableSorting={false}
            render={(_valor, linha) => (
              <span className={MAIUSCULA_INLINE}>
                {linha.ato}
                {linha.endereco_de_rede !== null && ` · ${linha.endereco_de_rede}`}
              </span>
            )}
          />
        </TablePrincipal2>
      </CardContent>

      <Sheet open={aberta !== null} onOpenChange={(abrir) => !abrir && definirAberta(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-xl [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">{aberta !== null ? `Registro #${aberta.id}` : "Registro"}</SheetTitle>
          </SheetHeader>
          {aberta !== null && <DetalheDoRegistro linha={aberta} aoFechar={() => definirAberta(null)} />}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

function DetalheDoRegistro({ linha, aoFechar }: { linha: LinhaDaTrilhaNaTela; aoFechar: () => void }) {
  const itens: [string, string | null][] = [
    ["Quando", dataHora(linha.momento)],
    ["Quem", quem(linha)],
    ["O quê", linha.operacao_rotulo],
    ["Sobre", sobre(linha)],
    ["Onde", linha.ato],
    ["Endereço de rede", linha.endereco_de_rede],
  ]
  return (
    <div className="flex flex-col gap-4 px-6 pt-3 pb-6">
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {itens.map(([rotulo, valor]) => (
          <div key={rotulo} className="contents">
            <dt className="text-(--tinta-2)">{rotulo}</dt>
            <dd className={cn("m-0 text-(--tinta) wrap-anywhere", MAIUSCULA_INLINE)}>{valor ?? "—"}</dd>
          </div>
        ))}
      </dl>
      {[
        ["De", linha.valor_anterior],
        ["Para", linha.valor_novo],
      ].map(([rotulo, valor]) => (
        <div key={rotulo} className="flex flex-col gap-1">
          <span className="text-xs font-semibold tracking-wide text-(--tinta-2) uppercase">{rotulo}</span>
          <p className="m-0 rounded-md border border-(--borda) bg-muted p-3 text-sm whitespace-pre-wrap text-(--tinta) wrap-anywhere">{valor ?? "—"}</p>
        </div>
      ))}
      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" onClick={aoFechar}>
          <Icones.Fechar />
          Fechar
        </Button>
      </div>
    </div>
  )
}
